import React, { useRef, useEffect, useState } from 'react';
import { connect } from 'react-redux';

const SqftTooltip = ({ isVisible, title, description, icon, position, targetRect }) => {
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [actualPosition, setActualPosition] = useState(position);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && targetRect && tooltipRef.current) {
      const updatePosition = () => {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top, left;
        let finalPosition = position;

        switch (position) {
          case 'right':
            top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
            left = targetRect.right + 10;

            // Check if tooltip goes beyond right edge of viewport
            if (left + tooltipRect.width > viewportWidth) {
              left = targetRect.left - tooltipRect.width - 10;
              finalPosition = 'left';
            }
            break;

          case 'left':
            top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
            left = targetRect.left - tooltipRect.width - 10;

            // Check if tooltip goes beyond left edge
            if (left < 0) {
              left = targetRect.right + 10;
              finalPosition = 'right';
            }
            break;

          case 'top':
            top = targetRect.top - tooltipRect.height - 10;
            left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);

            // Check if tooltip goes beyond top edge
            if (top < 0) {
              top = targetRect.bottom + 10;
              finalPosition = 'bottom';
            }
            break;

          case 'bottom':
            top = targetRect.bottom + 10;
            left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);

            // Check if tooltip goes beyond bottom edge
            if (top + tooltipRect.height > viewportHeight) {
              top = targetRect.top - tooltipRect.height - 10;
              finalPosition = 'top';
            }
            break;

          default:
            top = targetRect.top;
            left = targetRect.right + 10;
        }

        // Ensure tooltip doesn't go beyond viewport edges
        top = Math.max(10, Math.min(top, viewportHeight - tooltipRect.height - 10));
        left = Math.max(10, Math.min(left, viewportWidth - tooltipRect.width - 10));

        setTooltipPosition({ top, left });
        setActualPosition(finalPosition);
      };

      updatePosition();

      // Update position on scroll and resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible, targetRect, position]);

  if (!isVisible) {
    return null;
  }

  // Default icon if not provided
  const defaultIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div
      ref={tooltipRef}
      className={`sqft-tooltip sqft-tooltip--${actualPosition}`}
      style={{
        position: 'fixed',
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        zIndex: 999999,
      }}
    >
      {icon !== null && (
        <div className="sqft-tooltip__icon">
          {icon || defaultIcon}
        </div>
      )}

      <div className="sqft-tooltip__content">
        {title && (
          <h4 className="sqft-tooltip__title">
            {title}
          </h4>
        )}
        {description && (
          <p className="sqft-tooltip__description">
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (store) => ({
  isVisible: store.sqftTooltipState.isVisible,
  title: store.sqftTooltipState.title,
  description: store.sqftTooltipState.description,
  icon: store.sqftTooltipState.icon,
  position: store.sqftTooltipState.position,
  targetRect: store.sqftTooltipState.targetRect,
});

export default connect(mapStateToProps)(SqftTooltip);
